import { type ReactNode, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { Button } from "./Button";

const dialogStack: HTMLDialogElement[] = [];

const FOCUSABLE = "a[href], button, input, select, textarea, summary, [tabindex]";

function focusables(root: HTMLElement): HTMLElement[] {
  return [...root.querySelectorAll<HTMLElement>(FOCUSABLE)].filter((element) => {
    if ((element.hasAttribute("tabindex") && element.tabIndex < 0) || element.matches('input[type="hidden"]'))
      return false;
    const formControl = element.matches("button, input, select, textarea");
    if (formControl && element.hasAttribute("disabled")) return false;
    const firstChild = (parent: HTMLElement, tag: string) =>
      [...parent.children].find((child) => child.tagName === tag);
    if (
      element.tagName === "SUMMARY" &&
      !element.hasAttribute("tabindex") &&
      (element.parentElement?.tagName !== "DETAILS" ||
        firstChild(element.parentElement, "SUMMARY") !== element)
    )
      return false;
    const visibility = getComputedStyle(element).visibility;
    if (visibility === "hidden" || visibility === "collapse") return false;
    for (let ancestor: HTMLElement | null = element; ancestor; ancestor = ancestor.parentElement) {
      if (
        ancestor.hidden ||
        ancestor.inert ||
        ancestor.hasAttribute("inert") ||
        getComputedStyle(ancestor).display === "none"
      )
        return false;
      if (
        ancestor.tagName === "DETAILS" &&
        !ancestor.hasAttribute("open") &&
        !firstChild(ancestor, "SUMMARY")?.contains(element)
      )
        return false;
      if (
        formControl &&
        ancestor.tagName === "FIELDSET" &&
        ancestor.hasAttribute("disabled") &&
        !firstChild(ancestor, "LEGEND")?.contains(element)
      )
        return false;
    }
    return true;
  });
}

/**
 * A window over the page. Focus moves into it when it opens, Tab stays inside it, Esc and a click on the
 * backdrop close it, and on closing focus goes back to where it was (or to `returnFocus()`).
 */
export function Dialog({
  label,
  onClose,
  onKey,
  returnFocus,
  className,
  drawer = false,
  children,
}: {
  label: string;
  onClose: () => void;
  /** keys the window handles besides Tab and Esc (the gallery window uses the arrow keys) */
  onKey?: (event: KeyboardEvent) => void;
  /** where focus goes on closing; default: the element that had it when the window opened */
  returnFocus?: () => HTMLElement | null;
  className?: string;
  /** placed as a panel on the right edge (full screen on phones) instead of centred; behaviour is the same */
  drawer?: boolean;
  children: ReactNode;
}) {
  const panel = useRef<HTMLDialogElement>(null);
  const latest = useRef({ onClose, onKey, returnFocus });
  latest.current = { onClose, onKey, returnFocus };

  useEffect(() => {
    const before = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const root = panel.current;
    if (!root) return;
    const previous = dialogStack.at(-1);
    if (previous) {
      previous.inert = true;
      previous.setAttribute("aria-hidden", "true");
    }
    dialogStack.push(root);
    root.focus();
    document.body.classList.add("locked");

    // Keys are read on the document: they keep working when the focused control inside the window is
    // replaced (the gallery window swaps its content) and focus falls back to the page.
    const onKeyDown = (event: KeyboardEvent) => {
      const root = panel.current;
      if (!root || event.defaultPrevented || dialogStack.at(-1) !== root) return;
      if (event.key === "Escape") {
        event.preventDefault();
        event.stopImmediatePropagation();
        latest.current.onClose();
        return;
      }
      if (event.key === "Tab") {
        const list = focusables(root);
        const first = list[0];
        const last = list.at(-1);
        const current = document.activeElement;
        const inside = current instanceof HTMLElement && list.includes(current);
        if (!first || !last) {
          event.preventDefault();
          root.focus();
        } else if (!inside) {
          event.preventDefault();
          (event.shiftKey ? last : first).focus();
        } else if (event.shiftKey && current === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && current === last) {
          event.preventDefault();
          first.focus();
        }
        return;
      }
      latest.current.onKey?.(event);
    };
    document.addEventListener("keydown", onKeyDown);

    return () => {
      document.removeEventListener("keydown", onKeyDown);
      const index = dialogStack.indexOf(root);
      if (index !== -1) dialogStack.splice(index, 1);
      const remaining = dialogStack.at(-1);
      if (remaining) {
        remaining.inert = false;
        remaining.removeAttribute("aria-hidden");
      } else document.body.classList.remove("locked");
      const target = latest.current.returnFocus?.() ?? before;
      if (target?.isConnected) target.focus({ preventScroll: true });
    };
  }, []);

  return createPortal(
    <div className={drawer ? "modal-layer drawer-layer" : "modal-layer"}>
      {/* biome-ignore lint/a11y/noStaticElementInteractions: the backdrop is a pointer shortcut; the keyboard has Esc and the close button */}
      {/* biome-ignore lint/a11y/useKeyWithClickEvents: same */}
      <div className="modal-back" data-testid="backdrop" onClick={onClose} />
      <dialog
        open
        ref={panel}
        className={className ? `modal ${className}` : "modal"}
        aria-modal="true"
        aria-label={label}
        tabIndex={-1}
      >
        {children}
      </dialog>
    </div>,
    document.body,
  );
}

/** A yes/no question before something that cannot be undone. */
export function ConfirmDialog({
  title,
  confirmLabel,
  onConfirm,
  onCancel,
  returnFocus,
  children,
}: {
  title: string;
  confirmLabel: string;
  onConfirm: () => void;
  onCancel: () => void;
  /** where focus goes on closing when the asking control will be gone (a deleted row's button) */
  returnFocus?: () => HTMLElement | null;
  children?: ReactNode;
}) {
  return (
    <Dialog label={title} onClose={onCancel} className="modal-small" returnFocus={returnFocus}>
      <h2>{title}</h2>
      {children}
      <div className="acts">
        <Button variant="primary" onClick={onConfirm}>
          {confirmLabel}
        </Button>
        <Button onClick={onCancel}>그만두기</Button>
      </div>
    </Dialog>
  );
}
